import { bopApi } from '@/lib/api/client';

/** Repeating groups (directors, UBOs, partners) are free-form legacy maps */
export type LegacyRecordGroup = Array<Record<string, unknown>>;

/**
 * Legacy institution attachment (accounts-v0 shape). This is scraped legacy
 * onboarding data — every value must be treated as possibly missing, and keys
 * beyond the documented ones can appear (hence the index signature).
 */
export interface AccountInstitutionAttachment {
  // identity / registration
  companyName?: string;
  companyStreet?: string;
  companyCity?: string;
  companyPostCode?: string;
  companyCountry?: string;
  companyCountryCode?: string;
  companyTaxNumber?: string;
  companyRegistrationNumber?: string;
  countryCompanyRegistration?: string;
  /** typically MM/DD/YYYY */
  registrationDate?: string;
  /** typically MM/DD/YYYY */
  caseCreationDate?: string;
  /** typically YYYY-MM-DD */
  incorporationDate?: string;
  // fields referenced by the segment mapping (free-form legacy data, mostly strings)
  /** risk level */
  rLevel?: string;
  account_holder_id?: string;
  segment?: string;
  phoneNumber?: string;
  applicant_userId?: string;
  countryDomicile?: string;
  companyHasPeps?: string;
  companyLegalForm?: string;
  companyWebsite?: string;
  industry?: string;
  companyIsFinancial?: string;
  companyMiningActivities?: string;
  companyType?: string;
  signupNewsletter?: string;
  companyHasFoundationsTrust?: string;
  productSelections?: unknown;
  userHoldsJointRepresentativeRights?: string;
  haveLinksToUSBanks?: string;
  expectedMonthlyFiatDeposit?: string;
  expectedWithdrawal?: string;
  expectedMonthlyTradingVolume?: string;
  expectedMonthlyCryptoDeposit?: string;
  companyActingOnOwnBehalf?: string;
  expectedTotalInvestmentForThisYear?: string;
  purposeOfBitpandaAccount?: string;
  whatIsTheOriginOfTheFunds?: string;
  purposeOfBitpandaAccountOther?: string;
  // repeating groups
  adgCompanyDirectorInformation?: LegacyRecordGroup;
  adgUboInfo?: LegacyRecordGroup;
  adgBusinessPartners?: LegacyRecordGroup;
  /** exists in the data but is NOT rendered as a segment (raw JSON only) */
  adgAdditionalUsers?: LegacyRecordGroup;
  [additionalLegacyData: string]: unknown;
}

/** Legacy institution record from GET /v1/accounts/institutions. All fields except account_holder_id optional. */
export interface AccountInstitution {
  account_holder_id: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  segment?: string;
  // Read by the legacy UI at top level though absent from the documented typedef —
  // may be undefined on some records:
  isMarketMaker?: boolean;
  companyIsFinancial?: boolean | string;
  attachment?: AccountInstitutionAttachment;
}

export interface InstitutionSegmentField {
  label: string;
  value: unknown;
}

/** One detail-dialog section: heading + ordered key/value rows */
export interface InstitutionSegment {
  name: string;
  fields: InstitutionSegmentField[];
}

/** UI row: the original record + hoisted table fields + ordered display segments */
export interface InstitutionRow extends AccountInstitution {
  companyName?: string;
  companyStreet?: string;
  segments: InstitutionSegment[];
}

type FieldSpec = readonly [label: string, sourceKey: string];

const COMPANY_DETAILS_FIELDS: readonly FieldSpec[] = [
  ['Company Name', 'companyName'],
  ['Risk Level', 'rLevel'],
  ['Company Registration Country', 'countryCompanyRegistration'],
  // The ATTACHMENT copy of account_holder_id, not the record root (legacy parity)
  ['PID', 'account_holder_id'],
  ['Segment', 'segment'],
  ['Phone Number', 'phoneNumber'],
  ['Registration Date', 'registrationDate'],
  ['Country', 'companyCountry'],
  ['Applicant User ID', 'applicant_userId'],
];

// Duplicate 'Company Name' (also in Company Details) is intentional legacy behaviour — keep.
const FURTHER_COMPANY_DETAILS_FIELDS: readonly FieldSpec[] = [
  ['Company Has Peps', 'companyHasPeps'],
  ['Company Name', 'companyName'],
  ['Company Street', 'companyStreet'],
  ['Incorporation Date', 'incorporationDate'],
  ['Company Tax Number', 'companyTaxNumber'],
  ['Company Legal Form', 'companyLegalForm'],
  ['Company Website', 'companyWebsite'],
  ['Industry', 'industry'],
  ['Company Is Financial', 'companyIsFinancial'],
  ['Company Mining Activities', 'companyMiningActivities'],
  ['Company Type', 'companyType'],
  ['Company Post Code', 'companyPostCode'],
  ['Signup Newsletter', 'signupNewsletter'],
  ['Company Registration Number', 'companyRegistrationNumber'],
  ['Company Has Foundations Trust', 'companyHasFoundationsTrust'],
  ['Product Selections', 'productSelections'],
  ['User Holds Joint Representative Rights', 'userHoldsJointRepresentativeRights'],
  ['Have Links to US Banks', 'haveLinksToUSBanks'],
];

const AML_QUESTIONS_FIELDS: readonly FieldSpec[] = [
  ['Expected Monthly Fiat Deposit', 'expectedMonthlyFiatDeposit'],
  ['Expected Withdrawal', 'expectedWithdrawal'],
  ['Expected Monthly Trading Volume', 'expectedMonthlyTradingVolume'],
  ['Expected Monthly Crypto Deposit', 'expectedMonthlyCryptoDeposit'],
  ['Company Acting On Own Behalf', 'companyActingOnOwnBehalf'],
  ['Expected Total Investment For This Year', 'expectedTotalInvestmentForThisYear'],
  ['Purpose Of Bitpanda Account', 'purposeOfBitpandaAccount'],
  ['What Is The Origin Of The Funds', 'whatIsTheOriginOfTheFunds'],
  ['Purpose Of Bitpanda Account Other', 'purposeOfBitpandaAccountOther'],
];

const DIRECTOR_FIELDS: readonly FieldSpec[] = [
  ['First Name', 'directorFirstName'],
  ['Last Name', 'directorLastName'],
  ['Email', 'directorEmail'],
  ['Citizenship', 'directorCitizenship'],
  ['Country Code', 'directorCountryCode'],
  ['DOB', 'directorDob'],
  ['Age', 'directorAge'],
  ['External ID', 'directorExternalId'],
  ['KYC Applicant ID', 'directorKYCApplicantId'],
];

const UBO_FIELDS: readonly FieldSpec[] = [
  ['First Name', 'uboFirstName'],
  ['Last Name', 'uboLastName'],
  ['Date Of Birth', 'uboDateOfBirth'],
  ['Age', 'uboAge'],
  ['Place Of Birth', 'uboPob'],
  ['Country Domicile', 'uboCountryDomicile'],
  ['Country Residence', 'uboCountryResidence'],
  ['Citizenship', 'uboCitizenship'],
  ['Country Code', 'uboCountryCode'],
  ['Street Number', 'uboStreetNumber'],
  ['Post Code', 'uboPostCode'],
  ['City', 'uboCity'],
  ['Country', 'uboCountry'],
  ['Ownership Interest', 'ownershipInterest'],
  ['Employment Status', 'employmentStatus'],
  ['PEPs', 'peps'],
  ['External ID', 'uboExternalId'],
  ['U1', 'u1'],
  ['U2', 'u2'],
  ['U3', 'u3'],
  ['U4', 'u4'],
  ['Level', 'level'],
  ['KYC Applicant ID', 'uboKYCApplicantId'],
];

const BUSINESS_PARTNER_FIELDS: readonly FieldSpec[] = [
  ['Name', 'adgBusinessPartnersFullName'],
  // Deliberate fix: legacy labelled this value 'Email' but it is the
  // partner's ADDRESS (adgBusinessPartnersAddress) — relabelled to match the data.
  ['Address', 'adgBusinessPartnersAddress'],
];

const pickFields = (source: Record<string, unknown>, fields: readonly FieldSpec[]): InstitutionSegmentField[] =>
  fields.map(([label, sourceKey]) => ({ label, value: source[sourceKey] }));

// Never trust legacy data: a repeating group that isn't actually an array renders as no segments
const asGroup = (value: LegacyRecordGroup | undefined): LegacyRecordGroup => (Array.isArray(value) ? value : []);

/**
 * Builds the ordered detail-dialog segments: the fixed four,
 * then Directors, UBOs, Business Partners. An array is used instead of the
 * legacy keyed object so iteration order is guaranteed.
 */
export const buildSegments = (institution: AccountInstitution): InstitutionSegment[] => {
  const attachment = institution.attachment || {};
  return [
    { name: 'Company Details', fields: pickFields(attachment, COMPANY_DETAILS_FIELDS) },
    { name: 'Further Company Details', fields: pickFields(attachment, FURTHER_COMPANY_DETAILS_FIELDS) },
    {
      name: 'Main Account Holder',
      fields: [
        { label: 'First Name', value: institution.first_name },
        { label: 'Last Name', value: institution.last_name },
        { label: 'Email Address', value: institution.email_address },
        // Deliberate fix: legacy read institution.attachment.countryDomicile
        // without the `|| {}` fallback and threw on records with no attachment.
        { label: 'Country Domicile', value: institution.attachment?.countryDomicile },
      ],
    },
    { name: 'AML Questions', fields: pickFields(attachment, AML_QUESTIONS_FIELDS) },
    ...asGroup(attachment.adgCompanyDirectorInformation).map((director, index) => ({
      name: `Director ${index + 1}`,
      fields: pickFields(director, DIRECTOR_FIELDS),
    })),
    ...asGroup(attachment.adgUboInfo).map((ubo, index) => ({
      name: `UBO Info ${index + 1}`,
      fields: pickFields(ubo, UBO_FIELDS),
    })),
    // Deliberate fix: legacy segment name had a typo — 'Bussiness Partner N'
    ...asGroup(attachment.adgBusinessPartners).map((partner, index) => ({
      name: `Business Partner ${index + 1}`,
      fields: pickFields(partner, BUSINESS_PARTNER_FIELDS),
    })),
  ];
};

/**
 * Reproduces the legacy Nuxt mapping `{ ...institution, ...topLevelFields, segments }`:
 * companyName/companyStreet hoisted from the attachment, the root-level
 * flags restated verbatim, plus the display segments.
 */
export const mapInstitution = (institution: AccountInstitution): InstitutionRow => {
  const attachment = institution.attachment || {};
  return {
    ...institution,
    companyName: attachment.companyName,
    companyStreet: attachment.companyStreet,
    isMarketMaker: institution.isMarketMaker,
    companyIsFinancial: institution.companyIsFinancial,
    segment: institution.segment,
    segments: buildSegments(institution),
  };
};

export const mapInstitutions = (raw: AccountInstitution[]): InstitutionRow[] => raw.map(mapInstitution);

/** CSV export columns — exact legacy set and order */
export const INSTITUTIONS_CSV_COLUMNS = ['PID', 'First Name', 'Last Name', 'Email', 'Segment', 'Company'] as const;

export const toInstitutionCsvRow = (row: InstitutionRow): Record<string, unknown> => ({
  PID: row.account_holder_id,
  'First Name': row.first_name,
  'Last Name': row.last_name,
  Email: row.email_address,
  Segment: row.segment,
  Company: row.companyName,
});

export const getInstitutions = () => bopApi.get('v1/accounts/institutions').json<AccountInstitution[]>();
